import { Dialog } from "@headlessui/react"
import React from "react"
import { AcrCloudFileScan, AcrCloudMatch, AcrCloudAiDetection } from "../../../../../types"

// Safely parse a JSON string field, returning a fallback on failure
function safeParse<T>(value: string, fallback: T): T {
  try {
    return JSON.parse(value) as T
  } catch {
    return fallback
  }
}

const MatchList = ({ matches }: { matches: AcrCloudMatch[] }) => {
  if (!matches.length) {
    return (
      <div className="border border-gray-200 rounded-[6px] p-2 text-sm">
        <div className="text-sm text-gray-400 italic">No matches</div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {matches.map((match, i) => (
        <div key={i} className="border border-gray-200 rounded-[6px] p-2 text-sm">
          <div className="font-medium">{match.title}</div>
          <div className="text-gray-600">{match.artists?.map((a) => a.name).join(", ")}</div>
          <div className="flex gap-3 text-gray-500 text-xs mt-1">
            {match.external_ids?.isrc && <span>ISRC: {match.external_ids.isrc}</span>}
            {match.external_ids?.upc && <span>UPC: {match.external_ids.upc}</span>}
          </div>
        </div>
      ))}
    </div>
  )
}

const AiDetectionSection = ({ detection }: { detection: AcrCloudAiDetection[] }) => {
  if (!detection.length) {
    return <div className="text-sm text-gray-400 italic">No AI detection data</div>
  }

  return (
    <div className="flex flex-col gap-3">
      {detection.map((d, i) => (
        <div
          key={i}
          className="border border-gray-200 rounded-[6px] p-2 text-sm"
          style={{ background: d.prediction === "human" ? "" : "#f48e8ec8" }}
        >
          <div className="flex gap-4 mb-2">
            <span>
              <span className="font-medium">Prediction:</span> {d.prediction}
            </span>
            <span>
              <span className="font-medium">AI probability:</span> {d.ai_probability}%
            </span>
          </div>
          <div className="flex flex-wrap gap-x-2 gap-y-1 text-xs text-gray-600">
            {d.source_probabilities.map((sp, j) => (
              <span key={j}>
                {sp.source}({sp.probability}%)
                {j < d.source_probabilities.length - 1 ? " |" : ""}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

const AdminTakedownAcrCloudFileScanModal = ({
  selectedAcrCloudFileScanList,
  handleClose
}: {
  selectedAcrCloudFileScanList: AcrCloudFileScan[]
  handleClose: () => void
}) => {
  return (
    <Dialog open={selectedAcrCloudFileScanList.length > 0} onClose={() => handleClose()}>
      <div className="fixed w-screen h-screen top-0 left-0 z-250 flex items-center justify-center bg-[#0000004d]">
        <div
          className="p-6 shadow-md rounded-[10px] bg-[#fff] w-[900px] max-w-[95vw]"
          style={{ maxHeight: "90vh", overflowY: "auto" }}
        >
          {selectedAcrCloudFileScanList.map((acrCloudFileScan) => {
            const coverSongs = safeParse<AcrCloudMatch[]>(acrCloudFileScan.coverSongs, [])
            const music = safeParse<AcrCloudMatch[]>(acrCloudFileScan.music, [])
            const aiDetection = safeParse<AcrCloudAiDetection[]>(acrCloudFileScan.aiDetection, [])

            return (
              <div key={acrCloudFileScan.id} className="mb-6 pb-6 border-b border-gray-900 last:border-b-0">
                <div className="text-lg font-semibold mb-3">{acrCloudFileScan.name}</div>

                <div className="flex gap-4 mb-4">
                  <div className="flex-1 basis-1/2 min-w-0">
                    <div className="text-sm font-semibold text-gray-700 mb-2">Music</div>
                    <MatchList matches={music} />
                  </div>
                  <div className="flex-1 basis-1/2 min-w-0">
                    <div className="text-sm font-semibold text-gray-700 mb-2">Cover Songs</div>
                    <MatchList matches={coverSongs} />
                  </div>
                </div>

                <div>
                  <div className="text-sm font-semibold text-gray-700 mb-2">AI Detection</div>
                  <AiDetectionSection detection={aiDetection} />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </Dialog>
  )
}

export default AdminTakedownAcrCloudFileScanModal
